import { appId } from '../constants';
import { randomUUID } from './utils';

interface PendingUpload {
    id: string;
    file: File;
    path: string;
}

const DB_NAME = `${appId}-offline-uploads`;
const PENDING_UPLOADS_STORE = 'pending-image-uploads';
const DB_VERSION = 1;

let dbPromise: Promise<IDBDatabase> | null = null;

function getDB() {
    if (dbPromise) {
        return dbPromise;
    }
    dbPromise = new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onerror = () => {
            console.error('Error opening IndexedDB:', request.error);
            reject(request.error);
        };

        request.onupgradeneeded = event => {
            const db = (event.target as IDBRequest).result as IDBDatabase;
            if (!db.objectStoreNames.contains(PENDING_UPLOADS_STORE)) {
                db.createObjectStore(PENDING_UPLOADS_STORE, { keyPath: 'id' });
            }
        };

        request.onsuccess = event => {
            resolve((event.target as IDBRequest).result as IDBDatabase);
        };
    });
    return dbPromise;
}

/**
 * Adds a file to the queue of pending uploads.
 * @param file The file to upload.
 * @returns The unique ID for this pending upload.
 */
export async function addPendingUpload(file: File, path: string): Promise<string> {
    const id = randomUUID();
    const db = await getDB();
    const tx = db.transaction(PENDING_UPLOADS_STORE, 'readwrite');
    const store = tx.objectStore(PENDING_UPLOADS_STORE);
    const pendingUpload: PendingUpload = { id, file, path };

    return new Promise((resolve, reject) => {
        const request = store.add(pendingUpload);
        request.onsuccess = () => resolve(id);
        request.onerror = () => reject(request.error);
    });
}

/**
 * Retrieves a pending upload from the queue by its ID.
 */
export async function getPendingUpload(id: string): Promise<PendingUpload> {
    const db = await getDB();
    const tx = db.transaction(PENDING_UPLOADS_STORE, 'readonly');
    const store = tx.objectStore(PENDING_UPLOADS_STORE);

    return new Promise((resolve, reject) => {
        const request = store.get(id);
        request.onsuccess = event => {
            resolve((event.target as IDBRequest).result as PendingUpload);
        };
        request.onerror = () => reject(request.error);
    });
}

/**
 * Removes a pending upload from the queue by its ID.
 * @param id The ID of the pending upload to remove.
 */
export async function removePendingUpload(id: string): Promise<void> {
    const db = await getDB();
    const tx = db.transaction(PENDING_UPLOADS_STORE, 'readwrite');
    const store = tx.objectStore(PENDING_UPLOADS_STORE);

    return new Promise<void>((resolve, reject) => {
        const request = store.delete(id);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
    });
}